import React, { createContext, useContext, useState } from 'react';

/**
 * Interface defining the shape of the section context.
 * This context manages which form sections are disabled/enabled.
 */
interface SectionContextType {
  // Record of section names and their disabled state (true = disabled, false = enabled)
  disabledSections: Record<string, boolean>;
  toggleSectionDisabled: (sectionName: string, disabled: boolean) => void;
  isSectionDisabled: (sectionName: string) => boolean;
}

// Create the context with default values
const SectionContext = createContext<SectionContextType>({
  disabledSections: {},
  toggleSectionDisabled: () => {},
  isSectionDisabled: () => false,
});

/**
 * Custom hook to easily access the section context from any component
 */
export const useSectionContext = () => useContext(SectionContext);

/**
 * Provider component that makes the section context available to any
 * child component that calls useSectionContext().
 */
export const SectionProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  // State to track which sections are disabled
  const [disabledSections, setDisabledSections] = useState<Record<string, boolean>>({});

  /**
   * Toggle a section's disabled state
   * @param sectionName - The name of the section to update
   * @param disabled - Whether the section should be disabled (true) or enabled (false)
   */
  const toggleSectionDisabled = (sectionName: string, disabled: boolean) => {
    setDisabledSections(prev => ({
      ...prev,
      [sectionName]: disabled
    }));
  };

  /**
   * Check if a specific section is disabled
   * @param sectionName - The name of the section to check
   * @returns true if the section is disabled, false otherwise
   */
  const isSectionDisabled = (sectionName: string): boolean => {
    return !!disabledSections[sectionName];
  };

  return (
    <SectionContext.Provider value={{ 
      disabledSections, 
      toggleSectionDisabled,
      isSectionDisabled 
    }}>
      {children}
    </SectionContext.Provider>
  );
};